import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import storage from '../../lib/storage';
import { contentService } from '../../services';

const getSessionId = () => storage.get('guest_session_id') || '';

export default function useCreativeChat() {
  const { creativeId = '' } = useParams();
  const navigate = useNavigate();
  const [creative, setCreative] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  const sessionId = useMemo(() => {
    const token = storage.get('authToken');
    return token ? '' : getSessionId();
  }, []);

  const loadConversation = useCallback(async () => {
    if (!creativeId) return;
    setLoading(true);
    setError('');

    try {
      const data = await contentService.getCreativeConversation(creativeId, sessionId);
      if (!data?.success) {
        throw new Error(data?.message || 'Não foi possível carregar a conversa.');
      }

      setCreative(data.creative || null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (requestError) {
      setError(requestError?.message || 'Falha ao carregar conversa do criativo.');
    } finally {
      setLoading(false);
    }
  }, [creativeId, sessionId]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  useEffect(() => {
    const box = scrollRef.current;
    if (!box) return;
    box.scrollTop = box.scrollHeight;
  }, [messages, sending]);

  const sendMessage = useCallback(async () => {
    const next = String(draft || '').trim();
    if (!next || !creativeId || !creative) return;

    setSending(true);
    setError('');

    try {
      // Use /creative/:id/edit for image creatives (MP vNext)
      const isImageEditable = creative.creative_type === 'image' && creative.structure?.headline;
      let data;

      if (isImageEditable) {
        data = await contentService.editCreative(creativeId, {
          user_message: next,
          session_id: sessionId || undefined,
        }, sessionId);
      } else {
        data = await contentService.sendCreativeMessage(creativeId, {
          creative_type: creative.creative_type,
          current_structure: creative.structure,
          user_message: next,
        }, sessionId);
      }

      if (!data?.success) {
        throw new Error(data?.message || 'Não foi possível enviar a mensagem.');
      }

      setDraft('');
      setCreative(data.creative || creative);
      setMessages((prev) => {
        const combined = [...prev];
        if (data.user_message) combined.push(data.user_message);
        if (data.assistant_message) combined.push(data.assistant_message);
        return combined;
      });
    } catch (requestError) {
      setError(requestError?.message || 'Falha ao processar edição no chat.');
    } finally {
      setSending(false);
    }
  }, [creative, creativeId, draft, sessionId]);

  const openManualEditor = useCallback(() => {
    if (!creative) return;

    if (creative.creative_type === 'image' && creative.structure?.image_url) {
      navigate(`/manual-create?imageUrl=${encodeURIComponent(creative.structure.image_url)}`);
      return;
    }

    if (creative.creative_type === 'design' || creative.creative_type === 'carousel') {
      storage.set('mp_manual_editor_structure', {
        creative_id: creative.id,
        creative_type: creative.creative_type,
        structure: creative.structure,
      });
      navigate('/manual-create?fromCreative=1');
      return;
    }

    if (creative.creative_type === 'video' && creative.structure?.video_url) {
      navigate(`/manual-create?imageUrl=${encodeURIComponent(creative.structure.video_thumb || creative.structure.video_url)}`);
      return;
    }

    navigate('/manual-create');
  }, [creative, navigate]);

  // MP vNext: Upload de asset (logo, imagem) para criativo
  const [uploading, setUploading] = useState(false);

  const uploadAsset = useCallback(async (file, assetType = 'image') => {
    if (!file || !creativeId || !creative) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('asset', file);
      formData.append('asset_type', assetType);
      if (sessionId) formData.append('session_id', sessionId);

      const data = await contentService.uploadCreativeAsset(creativeId, formData, sessionId);

      if (!data?.success) {
        throw new Error(data?.message || 'Falha no upload do asset.');
      }

      if (data.assistant_message) {
        setMessages((prev) => [...prev, data.assistant_message]);
      }
    } catch (requestError) {
      setError(requestError?.message || 'Erro no upload.');
    } finally {
      setUploading(false);
    }
  }, [creative, creativeId, sessionId]);

  return {
    creative,
    messages,
    draft,
    setDraft,
    loading,
    sending,
    uploading,
    error,
    scrollRef,
    sendMessage,
    uploadAsset,
    openManualEditor,
    goToContents: () => navigate('/contents'),
  };
}
